/**
 * Tiny observable store + localStorage persistence.
 *
 * Listeners receive `(state, meta)` where `meta.changed` is a Set of top-level
 * keys that changed and `meta.source` optionally names the UI that caused the
 * change (so e.g. the tier editor doesn't re-render itself mid-typing).
 */
import { emptyCreator, sanitizeCreator } from './lib/creator.js';
import { defaultTiers } from './lib/tiers.js';

const STORAGE_KEY = 'tokdraw:session:v1';
const PERSISTED_KEYS = [
  'theme',
  'muted',
  'videoUrl',
  'source',
  'creator',
  'comments',
  'filters',
  'tiers',
  'activeTierId',
  'winners',
  'mode',
  'duration',
];

export const MODES = ['slot', 'wheel', 'countdown'];

export const THEMES = [
  { id: 'dark-minimal', label: 'Dark Minimal' },
  { id: 'cyber-neon', label: 'Cyber Neon' },
  { id: 'tiktok-vibe', label: 'TikTok Vibe' },
  { id: 'elegant-gold', label: 'Elegant Gold' },
];

export function initialState() {
  const tiers = defaultTiers();
  return {
    theme: 'dark-minimal',
    muted: false,
    record: false, // never persisted: screen capture needs a fresh permission each visit
    videoUrl: '',
    /** @type {null | {source: string, videoId: string, total: number, fetchedAt: string}} */
    source: null,
    /** Shown in Present View. @type {{username: string, displayName: string, avatar: string}} */
    creator: emptyCreator(),
    /** @type {Array<{id, username, displayName, comment, avatar?, createdAt?, likes?}>} */
    comments: [],
    filters: { keywords: '', uniqueOnly: true, exclude: '' },
    tiers,
    activeTierId: tiers[0].id,
    /** @type {Array<{id, tierId, tierName, prize, username, displayName, comment, commentId, drawnAt, mode, poolSize, voided}>} */
    winners: [],
    mode: 'slot',
    duration: 5,
  };
}

function loadPersisted() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const saved = JSON.parse(raw);
    return Object.fromEntries(PERSISTED_KEYS.filter((k) => k in saved).map((k) => [k, saved[k]]));
  } catch {
    return {};
  }
}

function sanitize(state) {
  const base = initialState();
  const s = { ...base, ...state };
  if (!THEMES.some((t) => t.id === s.theme)) s.theme = base.theme;
  if (!MODES.includes(s.mode)) s.mode = base.mode;
  s.duration = Math.min(10, Math.max(2, Number(s.duration) || base.duration));
  if (!Array.isArray(s.comments)) s.comments = [];
  if (!Array.isArray(s.winners)) s.winners = [];
  if (!Array.isArray(s.tiers) || !s.tiers.length) s.tiers = base.tiers;
  if (!s.tiers.some((t) => t.id === s.activeTierId)) s.activeTierId = s.tiers[0].id;
  s.filters = { ...base.filters, ...(s.filters ?? {}) };
  s.creator = sanitizeCreator(s.creator);
  s.record = false;
  return s;
}

function persist(state) {
  const data = Object.fromEntries(PERSISTED_KEYS.map((k) => [k, state[k]]));
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch {
    // Quota exceeded (huge comment lists) or storage blocked — keep the rest.
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...data, comments: [], source: null }));
    } catch {
      /* storage unavailable: session simply won't survive a reload */
    }
  }
}

export function clearPersisted() {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

export function createStore() {
  let state = sanitize(loadPersisted());
  const listeners = new Set();
  let saveTimer = 0;

  return {
    get: () => state,

    /** @param {object | ((s) => object)} patch */
    set(patch, meta = {}) {
      const next = typeof patch === 'function' ? patch(state) : patch;
      const changed = new Set(Object.keys(next).filter((k) => next[k] !== state[k]));
      if (!changed.size) return;
      state = { ...state, ...next };
      clearTimeout(saveTimer);
      saveTimer = setTimeout(() => persist(state), 250);
      for (const listener of listeners) listener(state, { ...meta, changed });
    },

    reset() {
      clearPersisted();
      const fresh = initialState();
      fresh.theme = state.theme;
      this.set(fresh, { source: 'reset' });
    },

    subscribe(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },

    flush() {
      clearTimeout(saveTimer);
      persist(state);
    },
  };
}

export const store = createStore();
addEventListener('pagehide', () => store.flush());
